// Direct-to-GCS resumable upload. The file bytes go straight from the browser
// to the session URL; they never pass through the API, Nginx or Cloudflare.

// GCS requires chunk sizes to be multiples of 256 KiB (except the last one).
export const CHUNK_SIZE = 32 * 1024 * 1024;
const MAX_RETRIES = 5;
const RETRY_BASE_DELAY_MS = 1000;

const RESUME_INCOMPLETE = 308;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const isRetryable = (status) =>
  status === 0 || status === 408 || status === 429 || status >= 500;

const putChunk = (uploadUrl, blob, start, total, onChunkProgress) =>
  new Promise((resolve) => {
    const end = start + blob.size - 1;
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader("Content-Range", `bytes ${start}-${end}/${total}`);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onChunkProgress(event.loaded);
      }
    };
    xhr.onload = () => resolve({ status: xhr.status, body: xhr.responseText });
    xhr.onerror = () => resolve({ status: 0, body: "" });
    xhr.ontimeout = () => resolve({ status: 0, body: "" });
    xhr.send(blob);
  });

const uploadChunk = async (uploadUrl, file, start, onChunkProgress) => {
  const end = Math.min(start + CHUNK_SIZE, file.size);
  let last = { status: 0, body: "" };

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
    if (attempt > 0) {
      onChunkProgress(0);
      // exponential backoff: 1s, 2s, 4s, 8s, 16s
      await wait(RETRY_BASE_DELAY_MS * 2 ** (attempt - 1));
    }
    // slice() is lazy: only one chunk is materialised at a time.
    last = await putChunk(
      uploadUrl,
      file.slice(start, end),
      start,
      file.size,
      onChunkProgress,
    );
    const isLastChunk = end === file.size;
    if (isLastChunk && (last.status === 200 || last.status === 201)) {
      return { end, done: true };
    }
    if (!isLastChunk && last.status === RESUME_INCOMPLETE) {
      return { end, done: false };
    }
    if (!isRetryable(last.status)) {
      break;
    }
  }

  throw new Error(
    last.status === 0
      ? "Upload failed: network error while sending file to storage"
      : `Upload failed: storage responded with status ${last.status}`,
  );
};

/**
 * Uploads `file` to a GCS resumable session URL in chunks.
 * `onProgress` receives a percentage between 0 and 100.
 */
export const uploadFileResumable = async (uploadUrl, file, onProgress) => {
  let offset = 0;
  while (offset < file.size) {
    // eslint-disable-next-line no-await-in-loop
    const { end, done } = await uploadChunk(uploadUrl, file, offset, (loaded) =>
      onProgress(((offset + loaded) / file.size) * 100),
    );
    offset = end;
    onProgress((offset / file.size) * 100);
    if (done) {
      return;
    }
  }
};
